"use client";

import { motion } from 'framer-motion';
import Link from 'next/link';
import {
  Mail,
  Phone,
  MapPin,
  Instagram,
  Linkedin,
  ExternalLink
} from 'lucide-react';
import LambLogo from '@/components/lamb-logo';
import TikTokIcon from '@/components/ui/tiktok-icon';
import { useLanguage } from '@/contexts/language-context';

interface FooterLink {
  href: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  external?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const footerSections: FooterSection[] = [
    {
      title: t('footer.navigation'),
      links: [
        { href: "/", label: t('footer.links.home') },
        { href: "/about", label: t('footer.links.about') },
        { href: "/contact", label: t('footer.links.contact') },
        { href: "https://demo.agnuscloud.com", label: t('footer.links.portfolio'), external: true }
      ]
    },
    {
      title: t('footer.legal'),
      links: [
        { href: "/terms", label: t('footer.links.terms') },
        { href: "/returns", label: t('footer.links.returns') },
        { href: "/privacy", label: t('footer.links.privacy') }
      ]
    },
    {
      title: t('footer.socialMedia'),
      links: [
        { href: "https://instagram.com/agnuscloud", label: "Instagram", icon: Instagram, external: true },
        { href: "https://linkedin.com/company/agnuscloud", label: "LinkedIn", icon: Linkedin, external: true },
        { href: "https://tiktok.com/@agnuscloud", label: "TikTok", icon: TikTokIcon, external: true }
      ]
    }
  ];

  const contactInfo = {
    address: {
      street: "Hoenderberg 28",
      postal: "3825CR Amersfoort",
      city: "Utrecht Nederland",
      note: t('footer.noVisitingAddress')
    },
    email: "info@agnuscloud.com",
    phone: "+31 85 - 488 354 3"
  };

  return (
    <footer className="relative bg-gradient-to-br from-deep-purple to-deep-purple/90 text-cream overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-20 -right-20 w-96 h-96 blob blob-1 opacity-10"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <motion.div
          className="absolute -bottom-20 -left-20 w-80 h-80 blob blob-2 opacity-10"
          animate={{
            x: [0, -40, 0],
            y: [0, 40, 0],
            scale: [1, 0.9, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5,
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Main footer content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Company info */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Link href="/" className="flex items-center space-x-3 mb-6">
                  <LambLogo className="w-12 h-12" />
                  <span className="text-2xl font-light text-cream">Agnus Cloud</span>
                </Link>

                {/* Contact info */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-cloud-lavender mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-cream/80 font-light">
                      <div>{contactInfo.address.street}</div>
                      <div>{contactInfo.address.postal}</div>
                      <div>{contactInfo.address.city}</div>
                      <div className="text-xs text-cream/60 mt-1">
                        {contactInfo.address.note}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-cloud-lavender" />
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-sm text-cream/80 hover:text-cloud-lavender transition-colors font-light"
                    >
                      {contactInfo.email}
                    </a>
                  </div>

                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-cloud-lavender" />
                    <a
                      href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                      className="text-sm text-cream/80 hover:text-cloud-lavender transition-colors font-light"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Footer sections */}
            {footerSections.map((section, index) => (
              <div key={section.title}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-lg font-light mb-4 text-cloud-lavender">
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {section.links.map((link) => (
                      <li key={link.label}>
                        <Link
                          href={link.href}
                          target={link.external ? "_blank" : undefined}
                          rel={link.external ? "noopener noreferrer" : undefined}
                          className="flex items-center gap-2 text-sm text-cream/80 hover:text-cloud-lavender transition-colors group font-light"
                        >
                          {link.icon && (
                            <link.icon className="w-4 h-4" />
                          )}
                          {link.label}
                          {link.external && (
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-cream/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col md:flex-row justify-between items-center gap-4"
            >
              <div className="text-sm text-cream/60 font-light">
                © {currentYear} Agnus Cloud
              </div>

              <div className="text-sm text-cream/60 font-light">
                {t('footer.subsidiaryText')}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;